import { useCallback, useEffect, useState } from 'react';
import { api, formatTarih } from '../lib/api';
import AraSec from '../ui/AraSec';
import KalemdenUrun, { type YeniUrunGirdi } from './KalemdenUrun';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';

type Grup = {
  kaynakId: string; ad: string; birim: string | null;
  satisSatir: number; alisSatir: number; toplamMiktar: number;
  sonFatura: string; sonTarih: string;
};
type Veri = {
  satirlar: Grup[];
  ozet: { bekleyenUrun: number; bagliUrun: number; satisSatir: number; alisSatir: number };
};
type Urun = { id: number; code: string; name: string; unit: string };
type Esitleme = {
  satis: { yeni: number }; alis: { yeni: number; otomatikBaglanan: number };
  stok: { dusen: number; transfer: number; kuyrukta: number; geriAlinan: number };
  hatalar: string[];
};

/**
 * PARAŞÜT ÜRÜN EŞLEME.
 *
 * Paraşüt kataloğunda ürün kodu yok (12.070 ürün, hiçbirinde). Bu ekran her
 * Paraşüt ürününü BİR KEZ bir ART ürününe bağlar; sonra o ürünün geçtiği tüm
 * satış ve alış satırları kendiliğinden eşleşir, stok düşer/girer.
 *
 * Sıralama: en çok faturada geçen ürün en üstte — ilk 10 bağlama, satırların
 * büyük kısmını çözer.
 */
export default function ParasutEsleme() {
  const { can } = useAuth();
  const [veri, setVeri] = useState<Veri | null>(null);
  const [urunler, setUrunler] = useState<Urun[]>([]);
  const [secim, setSecim] = useState<Record<string, string>>({});
  const [ara, setAra] = useState('');
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [calisan, setCalisan] = useState<string | null>(null);
  const [esitleniyor, setEsitleniyor] = useState(false);

  const yukle = useCallback(() => {
    api<Veri>('/api/parasut/eslesme-kuyrugu').then(setVeri).catch((e) => setHata((e as Error).message));
  }, []);
  useEffect(() => {
    yukle();
    api<{ satirlar: Urun[] }>('/api/urunler?durum=hepsi&limit=500')
      .then((r) => setUrunler(r.satirlar)).catch(() => undefined);
  }, [yukle]);

  async function esitle() {
    setHata(null); setEsitleniyor(true);
    try {
      const r = await api<Esitleme>('/api/parasut/esitle', { method: 'POST' });
      setBilgi(
        `Eşitlendi · satış +${r.satis.yeni}, alış +${r.alis.yeni} · ` +
        `stok düşen ${r.stok.dusen}, otomatik bağlanan alış ${r.alis.otomatikBaglanan}, ` +
        `kuyrukta ${r.stok.kuyrukta}` +
        (r.hatalar.length ? ` · HATA: ${r.hatalar.join(' | ')}` : '')
      );
      yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Eşitlenemedi');
    } finally {
      setEsitleniyor(false);
    }
  }

  /** Listede yoksa: Paraşüt ürününü ürün kartı olarak aç ve bağla. */
  async function urunAc(g: Grup, y: YeniUrunGirdi) {
    setHata(null); setCalisan(g.kaynakId);
    try {
      const r = await api<{ code: string; alisBaglanan: number; satisDusen: number }>('/api/parasut/esle', {
        method: 'POST', body: JSON.stringify({ kaynakId: g.kaynakId, ad: g.ad, yeniUrun: { ...y, birim: g.birim ?? undefined } }),
      });
      setBilgi(`"${g.ad}" için ${r.code} ürün kartı açıldı ve bağlandı · ${r.satisDusen} satış satırı stoktan düştü, ${r.alisBaglanan} alış satırı stoğa girdi`);
      yukle();
    } catch (e) { setHata(e instanceof Error ? e.message : 'Ürün açılamadı'); }
    finally { setCalisan(null); }
  }

  async function bagla(g: Grup) {
    const productId = Number(secim[g.kaynakId]);
    if (!productId) { setHata(`"${g.ad}" için önce ürün seçin`); return; }
    setHata(null); setCalisan(g.kaynakId);
    try {
      const r = await api<{ code: string; alisBaglanan: number; satisDusen: number; satisTransfer: number; kuyruktaKalan: number }>(
        '/api/parasut/esle',
        { method: 'POST', body: JSON.stringify({ kaynakId: g.kaynakId, productId, ad: g.ad }) }
      );
      setBilgi(
        `"${g.ad}" → ${r.code} bağlandı · ` +
        `${r.satisDusen} satış satırı stoktan düştü, ${r.alisBaglanan} alış satırı stoğa girdi` +
        (r.satisTransfer ? `, ${r.satisTransfer} faturasız→resmî transfer` : '')
      );
      yukle();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Bağlanamadı');
    } finally {
      setCalisan(null);
    }
  }

  const gorunen = (veri?.satirlar ?? []).filter((g) =>
    !ara || g.ad.toLocaleLowerCase('tr').includes(ara.toLocaleLowerCase('tr'))
  );

  return (
    <div className="ccd-stack">
      <div className="ccd-banner">
        <span className="ccd-banner-icon" aria-hidden="true">⇄</span>
        <div className="ccd-meta">
          Paraşüt'teki ürünlerin kodu yok; her birini <b>bir kez</b> ART ürününe bağlayın.
          Sonrası otomatik: o ürünün geçtiği tüm satış faturaları stoktan düşer, alış
          faturaları stoğa girer. En çok geçen ürün en üstte — ilk birkaç bağlama
          satırların çoğunu çözer. <b>Nakliye, palet, poşet gibi stok dışı kalemleri</b> bir
          HİZMET kategorili ürüne bağlayın: satır işlenir, stok hareketi yazılmaz.
        </div>
      </div>

      {veri && (
        <div className="ccd-grid ccd-grid-3">
          <div className="ccd-stat">
            <div className="ccd-stat-label">Bağlanmayı bekleyen Paraşüt ürünü <Ipucu id="esleme-kuyrugu" /></div>
            <div className="ccd-stat-value">{veri.ozet.bekleyenUrun}</div>
            <div className="ccd-stat-foot">{veri.ozet.satisSatir} satış + {veri.ozet.alisSatir} alış satırı</div>
          </div>
          <div className="ccd-stat">
            <div className="ccd-stat-label">Bağlı Paraşüt ürünü <Ipucu id="esleme-kuyrugu" /></div>
            <div className="ccd-stat-value">{veri.ozet.bagliUrun}</div>
            <div className="ccd-stat-foot">bir daha sorulmaz</div>
          </div>
          <div className="ccd-stat">
            <div className="ccd-stat-label">Eşitleme</div>
            <div className="ccd-stat-value" style={{ fontSize: 16 }}>
              {can('purchase.match') && (
                <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={esitle} disabled={esitleniyor}>
                  {esitleniyor ? 'Eşitleniyor…' : 'Şimdi eşitle'}
                </button>
              )}
            </div>
            <div className="ccd-stat-foot">işçi zaten 5 dakikada bir çalışıyor</div>
          </div>
        </div>
      )}

      {bilgi && <div className="ccd-banner"><span className="ccd-banner-icon">✓</span><span>{bilgi}</span></div>}
      {hata && (
        <div className="ccd-banner ccd-banner-danger">
          <span className="ccd-banner-icon">!</span><span>{hata}</span>
        </div>
      )}

      <div className="ccd-search" style={{ maxWidth: 380 }}>
        <span className="ccd-search-icon" aria-hidden="true">⌕</span>
        <input className="ccd-input" placeholder="Paraşüt ürün adında ara…"
               value={ara} onChange={(e) => setAra(e.target.value)} aria-label="Paraşüt ürünü ara" />
      </div>

      {!veri ? (
        <div className="ccd-inline-note">Yükleniyor…</div>
      ) : gorunen.length === 0 ? (
        <div className="ccd-empty">
          {veri.satirlar.length === 0
            ? 'Bağlanmayı bekleyen Paraşüt ürünü yok. Eşitleme sonrası yeni ürün gelirse burada görünür.'
            : `"${ara}" için sonuç yok.`}
        </div>
      ) : (
        <div className="ccd-table-wrap">
          <table className="ccd-table">
            <thead>
              <tr>
                <th className="ccd-th">Paraşüt ürünü</th>
                <th className="ccd-th ccd-td-num">Satış</th>
                <th className="ccd-th ccd-td-num">Alış</th>
                <th className="ccd-th ccd-td-num">Toplam</th>
                <th className="ccd-th">ART ürünü</th>
                {can('purchase.match') && <th className="ccd-th" />}
              </tr>
            </thead>
            <tbody>
              {gorunen.map((g) => (
                <tr key={g.kaynakId} className="ccd-tr">
                  <td className="ccd-td">
                    <b>{g.ad}</b>
                    <div className="ccd-meta">
                      son: {g.sonFatura} · {formatTarih(g.sonTarih)}
                    </div>
                  </td>
                  <td className="ccd-td ccd-td-num">{g.satisSatir || <span className="ccd-muted">—</span>}</td>
                  <td className="ccd-td ccd-td-num">{g.alisSatir || <span className="ccd-muted">—</span>}</td>
                  <td className="ccd-td ccd-td-num ccd-td-nowrap">
                    {Math.round(g.toplamMiktar * 100) / 100} {g.birim?.toLocaleLowerCase('tr')}
                  </td>
                  <td className="ccd-td">
                    <AraSec deger={secim[g.kaynakId] ?? ''} disabled={!can('purchase.match')}
                            degis={(v) => setSecim((x) => ({ ...x, [g.kaynakId]: v }))}
                            secenekler={urunler.map((u) => ({ id: u.id, ad: `${u.code} — ${u.name}` }))}
                            yerTutucu="Ürün ara: kod ya da ad…" />
                    {can('purchase.match') && can('product.write') && (
                      <div style={{ marginTop: 6 }}>
                        <KalemdenUrun ad={g.ad} birim={g.birim ?? null} calisiyor={calisan === g.kaynakId} ac={(y) => void urunAc(g, y)} />
                      </div>
                    )}
                  </td>
                  {can('purchase.match') && (
                    <td className="ccd-td">
                      <button className="ccd-btn ccd-btn-primary ccd-btn-sm"
                              disabled={calisan === g.kaynakId || !secim[g.kaynakId]}
                              onClick={() => bagla(g)}>
                        {calisan === g.kaynakId ? 'Bağlanıyor…' : 'Bağla'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
